(function () {

  /* Backing API domain — set window.debug = true (e.g. in dev tools console)
     before the rest of the page's scripts run to point every API call at a
     local instance instead of the deployed one. */
  var API_BASE = window.debug ? 'http://localhost:8888' : 'https://rivativ-api.netlify.app';

  /* Ghost member session token (a signed JWT), required by the /api/portfolios
     and /api/monthly endpoints. Same-origin call, cookie sent automatically;
     resolves to null for anonymous visitors. Cached for the page's lifetime
     since it doesn't change between calls. */
  var memberTokenPromise = null;
  function getMemberToken() {
    if (!memberTokenPromise) {
      memberTokenPromise = fetch('/members/api/session')
        .then(function (r) { return r.status === 204 ? null : r.text(); })
        .catch(function () { return null; });
    }
    return memberTokenPromise;
  }

  /* fetch() wrapper that attaches the member token as a Bearer header once
     it's resolved, for endpoints that now require authentication. */
  function authFetch(url) {
    return getMemberToken().then(function (token) {
      return fetch(url, token ? {headers: {Authorization: 'Bearer ' + token}} : undefined);
    });
  }

  /* Scroll position at which the cover tab bar pins below the header — shared by
     initStickyCoverTabs() (which triggers the pin) and initCoverTabs() (which
     jumps straight to it on tab click, so the tab bar is already in its compact
     pinned form instead of eating a whole screen of cover). */
  var PIN_SCROLL_Y = 420;

  /* Parsed once on load: #<panel>/<headingId> deep-links from the in-page nav.
     Consumed (and cleared) the first time the target heading is found in the DOM. */
  var initialHashTarget = (function () {
    var h = (location.hash || '').replace(/^#/, '');
    if (!h) return null;
    var slashIdx = h.indexOf('/');
    if (slashIdx === -1) return { panel: h, id: null };
    return { panel: h.slice(0, slashIdx), id: h.slice(slashIdx + 1) };
  }());

  /* ── Chart widget ──────────────────────────────────────── */
  function initChart(placeholder) {
    if (!placeholder) return;

    var isMarket  = placeholder.classList.contains('market-data');
    var strategy  = (placeholder.dataset.strategy  || '').trim();
    var fromDate  = (placeholder.dataset.fromdate  || '').trim();
    var toDate    = (placeholder.dataset.todate    || '').trim();
    var chartTitle = (placeholder.dataset.title   || '').trim();

    function splitProp(val) {
      var i = val.indexOf('|');
      return i === -1 ? { api: val, label: val } : { api: val.slice(0, i), label: val.slice(i + 1) };
    }
    var tickerProp    = splitProp((placeholder.dataset.ticker    || '').trim());
    var benchmarkProp = splitProp((placeholder.dataset.benchmark || '').trim());

    if (!isMarket && !strategy) return;
    if (isMarket  && !tickerProp.api) return;

    var portfolioName = isMarket
      ? tickerProp.label
      : 'This portfolio';
    var benchmarkName = benchmarkProp.label || 'Benchmark';

    var card = document.createElement('div');
    card.className = 'chart-card';
    var _parent = placeholder.parentNode;
    _parent.insertBefore(card, placeholder);
    while (placeholder.firstChild) { _parent.insertBefore(placeholder.firstChild, placeholder); }
    _parent.removeChild(placeholder);

    var isPerfPanelChart = !!card.closest('.pf-panel[data-panel="performance"]');
    var titleEl = document.createElement(isPerfPanelChart ? 'h2' : 'h3');
    titleEl.className = 'pf-title';
    titleEl.textContent = chartTitle || 'Normalised performance';
    card.appendChild(titleEl);
    buildToc(card.closest('.pf-panel'));

    var legendEl = document.createElement('div');
    legendEl.className = 'chart-legend';
    legendEl.innerHTML =
      '<span><span class="chart-legend-dot" style="background:#094385;"></span>' + portfolioName + '</span>' +
      '<span><span class="chart-legend-dot" style="background:#E7574E;"></span>' + benchmarkName + '</span>';

    var RANGES = [
      { label: '1M',  months: 1 },
      { label: '6M',  months: 6 },
      { label: '1Y',  months: 12 },
      { label: '3Y',  months: 36 },
    ];
    var DEFAULT_MONTHS = 36;

    var rangesEl = document.createElement('div');
    rangesEl.className = 'chart-ranges';

    var chartEl = document.createElement('div');
    Object.assign(chartEl.style, { width: '100%', height: '340px', position: 'relative' });
    var chartLoadingEl = document.createElement('div');
    Object.assign(chartLoadingEl.style, {
      position: 'absolute', inset: '0', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      color: 'rgba(255,255,255,0.35)', fontSize: '0.85rem',
      zIndex: '10', pointerEvents: 'none',
    });
    chartLoadingEl.textContent = 'Loading...';
    chartEl.appendChild(chartLoadingEl);
    card.appendChild(chartEl);

    var footerEl = document.createElement('div');
    footerEl.className = 'chart-footer';
    footerEl.appendChild(legendEl);
    footerEl.appendChild(rangesEl);
    card.appendChild(footerEl);

    var chart = LightweightCharts.createChart(chartEl, {
      width: chartEl.clientWidth,
      height: 340,
      layout: {
        attributionLogo: false,
        background: { color: 'transparent' },
        textColor: '#00000050',
        fontSize: 12,
      },
      grid: {
        vertLines: { visible: false },
        horzLines: { color: '#00000020' },
      },
      leftPriceScale: {
        visible: true,
        borderVisible: false,
        scaleMargins: { top: 0.1, bottom: 0.05 },
      },
      rightPriceScale: { visible: false },
      timeScale: { borderVisible: false, timeVisible: false, secondsVisible: false },
      crosshair: { mode: 0 },
      /* Crosshair/tooltip on hover still works — these only gate pan/zoom gestures.
         The 1M/6M/1Y/3Y range buttons are the only way to change what's plotted. */
      handleScroll: false,
      handleScale: false,
    });

    var seriesOpts = { lineWidth: 2, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false };
    var portfolioSeries = chart.addSeries(LightweightCharts.LineSeries, Object.assign({ color: '#094385' }, seriesOpts));
    var benchmarkSeries = chart.addSeries(LightweightCharts.LineSeries, Object.assign({ color: '#E7574E' }, seriesOpts));

    new ResizeObserver(function () {
      chart.resize(chartEl.clientWidth, chartEl.clientHeight);
      chart.timeScale().fitContent();
    }).observe(chartEl);

    var allPortfolio = [];
    var allBenchmark = [];

    function filterByMonths(data, months) {
      if (!months) return data;
      var cutoff = new Date();
      cutoff.setMonth(cutoff.getMonth() - months);
      var cutoffStr = cutoff.toISOString().slice(0, 10);
      return data.filter(function (d) { return d.time >= cutoffStr; });
    }

    /* Re-indexes a series so its first visible point is 100, so every range
       (and the initial load) reads as normalized performance from its own start. */
    function rebase(data) {
      var base = data.length ? data[0].value : 0;
      if (!base) return data;
      return data.map(function (d) { return { time: d.time, value: (d.value / base) * 100 }; });
    }

    function applyRange(months, activeBtn) {
      rangesEl.querySelectorAll('button').forEach(function (b) { b.classList.remove('active'); });
      if (activeBtn) activeBtn.classList.add('active');
      portfolioSeries.setData(rebase(filterByMonths(allPortfolio, months)));
      benchmarkSeries.setData(rebase(filterByMonths(allBenchmark, months)));
      chart.timeScale().fitContent();
    }

    RANGES.forEach(function (r) {
      var btn = document.createElement('button');
      btn.className = 'button button--flat';
      btn.textContent = r.label;
      if (r.months === DEFAULT_MONTHS) btn.classList.add('active');
      btn.addEventListener('click', function () { applyRange(r.months, btn); });
      rangesEl.appendChild(btn);
    });

    if (isMarket) {
      var url = API_BASE + '/api/markets/' + encodeURIComponent(tickerProp.api) +
        '?benchmark=' + encodeURIComponent(benchmarkProp.api) +
        (fromDate ? '&fromDate=' + encodeURIComponent(fromDate) : '') +
        (toDate   ? '&toDate='   + encodeURIComponent(toDate)   : '');

      fetch(url)
        .then(function (r) { return r.json(); })
        .then(function (arr) {
          function toSeries(points) {
            if (!points || !points.length) return [];
            var base = points[0].c;
            return points.map(function (p) {
              return {
                time:  new Date(p.t).toISOString().slice(0, 10),
                value: base ? (p.c / base) * 100 : 0,
              };
            });
          }

          allPortfolio = arr[0] ? toSeries(arr[0].data) : [];
          allBenchmark = arr[1] ? toSeries(arr[1].data) : [];

          if (!allPortfolio.length && !allBenchmark.length) return;
          var defaultBtn = rangesEl.querySelector('button.active');
          applyRange(DEFAULT_MONTHS, defaultBtn);
          chartLoadingEl.remove();
        })
        .catch(function (err) { console.error('Market chart fetch error:', err); });

    } else {
      fetch(API_BASE + '/api/performance/' + strategy)
      //fetch('/assets/data/performance.json?' + strategy)
        .then(function (r) { return r.json(); })
        .then(function (json) {
          if (json.error) return;

          var dates = Object.keys(json).sort();
          var firstKey, secondKey;
          dates.forEach(function (date) {
            var entry = json[date];
            if (!firstKey) {
              var keys = Object.keys(entry);
              firstKey  = keys[0];
              secondKey = keys[1];
            }
            allPortfolio.push({ time: date, value: entry[firstKey] });
            if (secondKey != null) allBenchmark.push({ time: date, value: entry[secondKey] });
          });

          if (!allPortfolio.length) return;

          var defaultBtn = rangesEl.querySelector('button.active');
          applyRange(DEFAULT_MONTHS, defaultBtn);
          chartLoadingEl.remove();
        })
        .catch(function (err) { console.error('Chart fetch error:', err); });
    }
  }

  /* ── Portfolio widget ──────────────────────────────────── */
  function initPortfolio() {
    var el = document.querySelector('.portfolio[data-strategy]');
    if (!el) return;
    var id = el.dataset.strategy;

    el.innerHTML = '<div class="pf-loading">Loading…</div>';

    Promise.all([
      authFetch(API_BASE + '/api/portfolios/' + id + '/dates')
        .then(function (r) { return r.json(); })
        .catch(function () { return []; }),
      authFetch(API_BASE + '/api/portfolios/' + id)
        .then(function (r) { return r.json(); })
    ]).then(function (results) {
      var dates = (results[0] || []).slice().sort();

      /* Standard tier (and Launch offer, which mirrors it) only gets weekly
         (Monday) refreshed allocations, so load the last available Monday's
         snapshot instead of the latest date. */
      var tier = getMemberTier();
      if (tier === 'Standard' || tier === 'Launch offer') {
        var monday = lastAvailableMonday(dates);
        if (monday) {
          loadPortfolio(el, dates, monday);
          return;
        }
      }

      var data = normalizePortfolioData(results[1]);
      renderPortfolio(el, data, dates, data.date || (dates.length ? dates[dates.length - 1] : null));
    }).catch(function (err) { console.error('Portfolio load error:', err); });
  }

  /* Finds the most recent date in a sorted (ascending) list of 'YYYY-MM-DD' dates
     that falls on a Monday. */
  function lastAvailableMonday(dates) {
    for (var i = dates.length - 1; i >= 0; i--) {
      if (new Date(dates[i] + 'T00:00:00Z').getUTCDay() === 1) return dates[i];
    }
    return null;
  }

  /* Re-fetches a single date's portfolio snapshot, e.g. after a date-picker selection */
  function loadPortfolio(el, dates, date) {
    var id = el.dataset.strategy;
    el.innerHTML = '<div class="pf-loading">Loading…</div>';

    authFetch(API_BASE + '/api/portfolios/' + id + '?date=' + encodeURIComponent(date))
      .then(function (r) { return r.json(); })
      .then(function (data) { renderPortfolio(el, normalizePortfolioData(data), dates, date); })
      .catch(function (err) { console.error('Portfolio load error:', err); });
  }

  function normalizePortfolioData(raw) {
    function parseOccTicker(ticker) {
      if (ticker.slice(0, 2) === 'O:') {
        var sym        = ticker.slice(2);
        var strikeRaw  = sym.slice(-8);
        var optType    = sym.slice(-9, -8);
        var dateRaw    = sym.slice(-15, -9);
        var underlying = sym.slice(0, -15);
        return {
          longhand: ticker,
          ticker: underlying,
          type:   optType === 'C' ? 'Call' : 'Put',
          strike: parseInt(strikeRaw, 10) / 1000,
          expiry: '20' + dateRaw.slice(0, 2) + '-' + dateRaw.slice(2, 4) + '-' + dateRaw.slice(4, 6),
        };
      }
      return { ticker: ticker, type: 'Stock', strike: null, expiry: null };
    }

    var rawPositions = raw['Portfolio Positions'] || {};
    var positions = Object.keys(rawPositions).map(function (ticker) {
      var p      = rawPositions[ticker];
      var parsed = parseOccTicker(ticker);
      return {
        ticker:        parsed.ticker,
        type:          parsed.type,
        strike:        parsed.strike,
        expiry:        parsed.expiry,
        qty:           p.qty,
        avg_price:     p.average_price,
        pandl:         p["PnL"],
        longhand:      parsed.longhand || parsed.ticker,
        greeks:        parsed.longhand && p.iv !== undefined ? { iv: p.iv, delta: p.delta, gamma: p.gamma, vega: p.vega, theta: p.theta, rho: p.rho } : null,
      };
    });

    var trades = (raw['Portfolio Trades'] || []).map(function (t) {
      var parsed = parseOccTicker(t.contract_ticker);
      return {
        ticker: parsed.longhand,
        qty:    t.qty,
        price:  t.price,
      };
    });

    return {
      cash:           raw['Portfolio Cash'],
      nav:            raw['Portfolio NAV'],
      date:           raw['Date'] || null,
      positions:      positions,
      trades:         trades,
      portfolioGreeks: raw['Portfolio Greeks'] || null,
    };
  }

  function renderPortfolio(el, data, dates, selectedDate) {
    var memberTier = getMemberTier();

    /* Strategy 4 is our advanced strategy; Standard and Pro members (and the
       Launch offer / Introductory offer tiers that mirror them) don't get
       access to its allocations, so swap the whole widget for an upsell note. */
    if (el.dataset.strategy === '4' && ['Standard', 'Launch offer', 'Pro', 'Introductory offer'].indexOf(memberTier) !== -1) {
      renderUpgradeNote(el, 'To access our advanced strategies, please <a href="#/portal">upgrade your membership</a>.');
      return;
    }

    el.innerHTML = '';
    var compact = el.classList.contains('pf-compact');

    var dlBtn = document.createElement('button');
    dlBtn.className = 'button';
    dlBtn.textContent = 'Download';

    /* Date, cash and NAV */
    function buildSummary() {
      var summaryEl = document.createElement('div');
      summaryEl.className = 'pf-summary' + (compact ? ' pf-summary-large' : '');

      if (data.date) {
        var dateEl = document.createElement('div');
        dateEl.className = 'pf-date';
        dateEl.innerHTML =
          '<div class="pf-date-label">Date</div>' +
          '<div class="pf-date-value">' + fmtExpiry(data.date) + '</div>';
        summaryEl.appendChild(dateEl);
      }

      var navEl = document.createElement('div');
      navEl.className = 'pf-nav';
      navEl.innerHTML =
        '<div class="pf-nav-label">NAV</div>' +
        '<div class="pf-nav-value">' + fmtCurrency(data.nav || 0) + '</div>';
      summaryEl.appendChild(navEl);

      var cashEl = document.createElement('div');
      cashEl.className = 'pf-cash';
      cashEl.innerHTML =
        '<div class="pf-cash-label">Cash</div>' +
        '<div class="pf-cash-value">' + fmtCurrency(data.cash || 0) + '</div>';
      summaryEl.appendChild(cashEl);

      return summaryEl;
    }

    if (!compact) {
      var header = document.createElement('div');
      header.className = 'pf-header';

      var titleEl = document.createElement('h3');
      titleEl.className = 'pf-title';
      titleEl.textContent = el.dataset.title || 'Asset allocation';
      header.appendChild(titleEl);
      header.appendChild(dlBtn);
      el.appendChild(header);
      el.appendChild(buildSummary());
    }

    /* Sections */
    var TAB_DEFS = [
      { label: 'Positions', cols: posCols(),    rows: posRows(data.positions), emptyMessage: 'No positions on this date.' },
      { label: 'Greeks',    cols: greekCols(),  rows: greekRows(data.positions), emptyMessage: 'No positions on this date.' },
      { label: 'Trades',    cols: tradeCols(),  rows: tradeRows(data.trades), emptyMessage: 'No new trades on this date.' },
    ];
    if (el.dataset.strategy !== '4' && data.portfolioGreeks) {
      TAB_DEFS.push({ label: 'Portfolio Greeks', cols: portfolioGreekCols(), rows: portfolioGreekRows(data.portfolioGreeks) });
    }

    if (compact) {
      /* Stacked sections, no tab switcher, no inline weighting bars.
         Date picker and download live under the TOC, same as the Monthly Report tab. */
      TAB_DEFS.forEach(function (def, i) {
        var sectionWrap = document.createElement('div');
        sectionWrap.className = 'pf-section';
        var sectionHeader = document.createElement('div');
        sectionHeader.className = 'pf-header';
        var sectionTitle = document.createElement(i === 0 ? 'h2' : 'h3');
        sectionTitle.className = 'pf-section-title';
        sectionTitle.textContent = def.label;
        sectionHeader.appendChild(sectionTitle);
        sectionWrap.appendChild(sectionHeader);
        if (i === 0) sectionWrap.appendChild(buildSummary());
        var sectionEl = document.createElement('div');
        sectionWrap.appendChild(sectionEl);
        renderTable(sectionEl, def);
        el.appendChild(sectionWrap);
      });
      addSeparators(el);
      var panel = el.closest('.pf-panel');
      buildToc(panel);
      if (memberTier === 'Standard' || memberTier === 'Launch offer') {
        buildAllocationsDateNote(panel, 'Allocations refreshed every Monday. For daily updates, please <a href="#/portal">upgrade your membership</a>.');
      } else {
        buildAllocationsDatePicker(panel, dates, selectedDate, function (date) {
          loadPortfolio(el, dates, date);
        });
      }
      var dlTocBtn = buildTocButton(panel, 'pf-toc-download', 'Download', function () {
        dlTocBtn.disabled = true;
        dlTocBtn.textContent = 'Preparing…';
        downloadPortfolioExcel(data, el.dataset.strategy, function () {
          dlTocBtn.disabled = false;
          dlTocBtn.textContent = 'Download';
        });
      });
    } else {
      var activeIdx = 0;

      var tabsEl = document.createElement('div');
      tabsEl.className = 'pf-tabs';
      el.appendChild(tabsEl);

      var contentEl = document.createElement('div');
      el.appendChild(contentEl);

      var tabBtns = TAB_DEFS.map(function (def, i) {
        var btn = document.createElement('button');
        btn.className = 'pf-tab' + (i === 0 ? ' active' : '');
        btn.textContent = def.label;
        btn.addEventListener('click', function () {
          tabBtns.forEach(function (b) { b.classList.remove('active'); });
          btn.classList.add('active');
          activeIdx = i;
          if (i === 0) {
            renderPositionsTable(contentEl, data);
          } else {
            renderTable(contentEl, TAB_DEFS[i]);
          }
        });
        tabsEl.appendChild(btn);
        return btn;
      });

      renderPositionsTable(contentEl, data);
    }

    dlBtn.addEventListener('click', function () {
      downloadCSV('positions', posCols(), posRows(data.positions));
      downloadCSV('trades',    tradeCols(), tradeRows(data.trades));
    });
  }

  /* Column / row builders */
  function posCols() {
    return [
      { label: 'Tickers',       align: '' },
      { label: 'Type',          align: 'c' },
      { label: 'Strike',        align: 'c' },
      { label: 'Expiry',        align: 'c' },
      { label: 'Qty',           align: 'r' },
      { label: 'Avg price',     align: 'r' },
      { label: 'P&L',           align: 'r' },
    ];
  }
  function posRows(positions) {
    return positions.map(function (p) {
      return [
        p.ticker,
        p.type,
        p.strike !== null ? p.strike.toFixed(1) : '-',
        p.expiry ? fmtExpiry(p.expiry) : '-',
        p.qty,
        p.avg_price !== null ? p.avg_price.toFixed(3) : '-',
        p.pandl !== null ? p.pandl.toFixed(2) : '-',
      ];
    });
  }

  function greekCols() {
    return [
      { label: 'Tickers', align: '' },
      { label: 'IV',      align: 'r' },
      { label: 'Delta',   align: 'r' },
      { label: 'Gamma',   align: 'r' },
      { label: 'Vega',    align: 'r' },
      { label: 'Theta',   align: 'r' },
      { label: 'Rho',     align: 'r' },
    ];
  }
  function greekRows(positions) {
    return positions.filter(function (p) {
      return p.longhand && p.greeks;
    }).map(function (p) {
      var g = p.greeks;
      return [
        p.longhand,
        g ? (g.iv * 100).toFixed(1) + '%' : '-',
        g ? g.delta.toFixed(4) : '-',
        g ? g.gamma.toFixed(4) : '-',
        g ? g.vega.toFixed(4)  : '-',
        g ? g.theta.toFixed(4) : '-',
        g ? g.rho.toFixed(4)   : '-',
      ];
    });
  }

  function tradeCols() {
    return [
      { label: 'Tickers', align: '' },
      { label: 'Qty',     align: 'r' },
      { label: 'Price',   align: 'r' },
    ];
  }
  function tradeRows(trades) {
    return trades.map(function (t) {
      return [
        t.ticker,
        t.qty,
        t.price !== null ? t.price.toFixed(3) : '-',
      ];
    });
  }

  function portfolioGreekCols() {
    return [
      { label: 'Metric', align: '' },
      { label: 'Value',  align: 'r' },
    ];
  }
  function portfolioGreekRows(greeks) {
    var EXCLUDED = ['delta_to_nav'];
    return Object.keys(greeks)
      .filter(function (k) { return EXCLUDED.indexOf(k) === -1; })
      .map(function (k) {
        return [
          k.split('_').map(function (w) {
            if (w === 'ptf') return 'Portfolio';
            if (w === 'nav') return 'NAV';
            return w.charAt(0).toUpperCase() + w.slice(1);
          }).join(' '),
          (+greeks[k]).toFixed(4),
        ];
      });
  }

  /* Table renderer */
  function renderTable(container, def) {
    container.innerHTML = '';

    if (!def.rows.length && def.emptyMessage) {
      var msg = document.createElement('p');
      msg.className = 'pf-empty';
      msg.textContent = def.emptyMessage;
      container.appendChild(msg);
      return;
    }

    var table = document.createElement('table');
    table.className = 'pf-table';

    var thead = document.createElement('thead');
    var htr = document.createElement('tr');
    def.cols.forEach(function (col) {
      var th = document.createElement('th');
      th.textContent = col.label;
      if (col.align) th.className = col.align;
      htr.appendChild(th);
    });
    thead.appendChild(htr);
    table.appendChild(thead);

    var tbody = document.createElement('tbody');
    def.rows.forEach(function (row) {
      var tr = document.createElement('tr');
      row.forEach(function (cell, i) {
        var td = document.createElement('td');
        td.textContent = cell;
        if (def.cols[i].align) td.className = def.cols[i].align;
        tr.appendChild(td);
      });
      tbody.appendChild(tr);
    });
    table.appendChild(tbody);
    var wrap = document.createElement('div');
    wrap.className = 'pf-table-wrap';
    wrap.appendChild(table);
    container.appendChild(wrap);
  }

  /* CSV download */
  function downloadCSV(name, cols, rows) {
    var lines = [cols.map(function (c) { return c.label; }).join(',')];
    rows.forEach(function (row) {
      lines.push(row.map(function (v) {
        var s = String(v);
        return s.indexOf(',') > -1 ? '"' + s + '"' : s;
      }).join(','));
    });
    var blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    var a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = name + '.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  }

  /* Lazily loads SheetJS (xlsx) from a CDN, same deferred-script pattern initChart()
     uses for LightweightCharts. Queues { cb, onError } pairs if a load is already in
     flight, and runs the right side of each pair whichever way the load resolves so
     callers (e.g. a Download button re-enabling itself) always get notified. */
  function loadSheetJS(cb, onError) {
    if (window.XLSX) { cb(); return; }
    if (loadSheetJS._pending) { loadSheetJS._pending.push({ cb: cb, onError: onError }); return; }
    loadSheetJS._pending = [{ cb: cb, onError: onError }];
    var s = document.createElement('script');
    s.src = 'https://cdn.sheetjs.com/xlsx-latest/package/dist/xlsx.full.min.js';
    s.onload = function () {
      var pending = loadSheetJS._pending;
      loadSheetJS._pending = null;
      pending.forEach(function (p) { p.cb(); });
    };
    s.onerror = function () {
      var pending = loadSheetJS._pending;
      loadSheetJS._pending = null;
      console.error('Failed to load SheetJS');
      pending.forEach(function (p) { if (p.onError) p.onError(); });
    };
    document.head.appendChild(s);
  }

  /* Builds a sheet-ready array-of-arrays from a cols/rows pair, reusing the same
     formatting posCols/greekCols/etc. already produce for the on-page tables and CSV
     export. Cells in right-aligned ('r') columns are the numeric convention used
     throughout this file, so they're parsed back to real numbers here to keep the
     Excel columns numeric instead of text. */
  function sheetAoa(cols, rows) {
    var header = cols.map(function (c) { return c.label; });
    var body = rows.map(function (row) {
      return row.map(function (cell, i) {
        if (cols[i].align !== 'r' || cell === '-' || cell === '') return cell;
        var n = parseFloat(String(cell).replace('%', ''));
        return isNaN(n) ? cell : n;
      });
    });
    return [header].concat(body);
  }

  /* Downloads the Daily Allocations panel as a single .xlsx workbook, one sheet per
     section (matching the full/non-compact tab switcher): Positions (with Date/NAV/Cash
     as header rows), Greeks, Trades, and Portfolio Greeks when present. onDone (if given)
     fires once regardless of success/failure, so a caller can reset a "Preparing…" button. */
  function downloadPortfolioExcel(data, strategyId, onDone) {
    loadSheetJS(function () {
      var wb = XLSX.utils.book_new();

      var summaryRows = [];
      if (data.date) summaryRows.push(['Date', fmtExpiry(data.date)]);
      summaryRows.push(['NAV', fmtCurrency(data.nav || 0)]);
      summaryRows.push(['Cash', fmtCurrency(data.cash || 0)]);
      summaryRows.push([]);

      var positionsAoa = summaryRows.concat(sheetAoa(posCols(), posRows(data.positions)));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(positionsAoa), 'Positions');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(sheetAoa(greekCols(), greekRows(data.positions))), 'Greeks');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(sheetAoa(tradeCols(), tradeRows(data.trades))), 'Trades');

      if (strategyId !== '4' && data.portfolioGreeks) {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(sheetAoa(portfolioGreekCols(), portfolioGreekRows(data.portfolioGreeks))), 'Portfolio Greeks');
      }

      var filename = (coverPostTitle() + ' - Daily allocations' + (data.date ? ' - ' + fmtExpiry(data.date) : ''))
        .replace(/[^a-z0-9-_ ]/gi, '').trim();
      XLSX.writeFile(wb, filename + '.xlsx');
      if (onDone) onDone();
    }, onDone);
  }

  /* Exports a .pf-panel's content as a clean, unformatted Word file. Word opens
     plain HTML saved with a .doc extension natively, so no docx library is needed;
     stripping class/style/id attributes and dropping UI-only elements (buttons,
     selects, charts, separators) keeps the output to plain headings/paragraphs/tables. */
  function exportPanelAsWord(panel, title) {
    var content = panel.querySelector('.pf-panel-content');
    if (!content) return;

    var clone = content.cloneNode(true);
    clone.querySelectorAll('button, select, label, canvas, svg, hr, .chart-ranges, .chart-legend, .chart-footer, .pf-loading, .pf-empty')
      .forEach(function (n) { n.remove(); });
    clone.querySelectorAll('*').forEach(function (el) {
      el.removeAttribute('class');
      el.removeAttribute('style');
      el.removeAttribute('id');
      el.removeAttribute('data-slug');
    });
    /* Style the report's own tables before we add any wrapper tables below, so
       this pass doesn't touch those. */
    clone.querySelectorAll('table').forEach(function (table) {
      table.setAttribute('width', '100%');
      table.setAttribute('style', 'width:100%;border-collapse:collapse;');
    });

    /* Direct <img> width/height (attributes or CSS) turned out unreliable in Word's
       legacy HTML-import filter — two rounds of both still overflowed the margin.
       Table *cell* width is one of the few things that filter consistently honors,
       so pin each image by wrapping it in its own single-cell, fixed-width table
       instead of sizing the <img> directly. */
    var liveImgs = content.querySelectorAll('img');
    clone.querySelectorAll('img').forEach(function (img, i) {
      var live = liveImgs[i];
      var w = (live && live.naturalWidth) || 0;
      var h = (live && live.naturalHeight) || 0;
      var widthPx = 540;
      var heightPx = (w && h) ? Math.round(widthPx * h / w) : null;

      img.removeAttribute('width');
      img.removeAttribute('height');
      img.setAttribute('width', String(widthPx));
      img.setAttribute('style', 'width:100%;' + (heightPx ? 'height:' + heightPx + 'px;' : ''));
      if (heightPx) img.setAttribute('height', String(heightPx));

      var table = document.createElement('table');
      table.setAttribute('width', String(widthPx));
      table.setAttribute('style', 'width:' + widthPx + 'px;border-collapse:collapse;');
      var tr = document.createElement('tr');
      var td = document.createElement('td');
      td.setAttribute('style', 'padding:0;border:none;');
      img.parentNode.insertBefore(table, img);
      td.appendChild(img);
      tr.appendChild(td);
      table.appendChild(tr);
    });

    var html =
      '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">' +
      '<head><meta charset="utf-8"><title>' + title + '</title>' +
      '<style>' +
        '@page WordSection1 { size: 8.5in 11in; margin: 1in; }' +
        'div.WordSection1 { page: WordSection1; }' +
        'img { max-width: 100%; height: auto; }' +
        'table { width: 100%; border-collapse: collapse; margin: 12px 0; }' +
        'th, td { padding: 6px 10px; border-bottom: 1px solid #ccc; text-align: left; }' +
        'th { font-weight: bold; }' +
      '</style></head>' +
      '<body><div class="WordSection1"><h1>' + title + '</h1>' + clone.innerHTML + '</div></body></html>';

    var blob = new Blob(['﻿', html], { type: 'application/msword' });
    var a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = title.replace(/[^a-z0-9-_ ]/gi, '').trim() + '.doc';
    a.click();
    URL.revokeObjectURL(a.href);
  }

  function coverPostTitle() {
    var el = document.querySelector('.portfolio-cover .cover-description');
    return el ? el.textContent.trim() : 'Portfolio';
  }

  /* Appended after buildToc() (which clears .pf-toc) so it survives every rebuild.
     Shared by every panel's "under the TOC" button (Word export, CSV export, ...). */
  function buildTocButton(panel, className, label, onClick) {
    var toc = panel.querySelector('.pf-toc');
    if (!toc) return null;
    var old = toc.querySelector('.' + className);
    if (old) old.remove();

    var wrap = document.createElement('div');
    wrap.className = className;
    var btn = document.createElement('button');
    btn.className = 'button';
    btn.textContent = label;
    btn.addEventListener('click', onClick);
    wrap.appendChild(btn);
    toc.appendChild(wrap);
    return btn;
  }

  function buildTocDownload(panel, title) {
    buildTocButton(panel, 'pf-toc-download', 'Download', function () { exportPanelAsWord(panel, title); });
  }

  /* Date picker for the Daily Allocations tab, appended under the TOC (same slot
     pattern as buildTocButton / buildMonthlyDateSelect). Only dates present in
     `dates` (from /api/portfolios/{id}/dates) are selectable — everything else in
     the calendar grid renders disabled. */
  function buildAllocationsDatePicker(panel, dates, selectedDate, onSelect) {
    var toc = panel.querySelector('.pf-toc');
    if (!toc) return;
    var old = toc.querySelector('.pf-toc-date');
    if (old) old.remove();
    if (!dates || !dates.length) return;

    var dateSet = {};
    dates.forEach(function (d) { dateSet[d] = true; });

    function pad2(n) { return (n < 10 ? '0' : '') + n; }

    var wrap = document.createElement('div');
    wrap.className = 'pf-toc-date';

    var heading = document.createElement('div');
    heading.className = 'pf-toc-heading';
    heading.textContent = 'Date';
    wrap.appendChild(heading);

    var pickerWrap = document.createElement('div');
    pickerWrap.className = 'pf-datepicker';
    wrap.appendChild(pickerWrap);

    var toggleBtn = document.createElement('button');
    toggleBtn.type = 'button';
    toggleBtn.className = 'pf-toc-select pf-datepicker-toggle';
    toggleBtn.textContent = selectedDate ? fmtExpiry(selectedDate) : 'Select date';
    pickerWrap.appendChild(toggleBtn);

    var calendarEl = document.createElement('div');
    calendarEl.className = 'pf-datepicker-cal';
    calendarEl.hidden = true;
    pickerWrap.appendChild(calendarEl);

    var initial   = selectedDate ? selectedDate.split('-') : null;
    var viewYear  = initial ? +initial[0] : new Date().getFullYear();
    var viewMonth = initial ? (+initial[1] - 1) : new Date().getMonth();

    function renderCalendar() {
      calendarEl.innerHTML = '';

      var nav = document.createElement('div');
      nav.className = 'pf-datepicker-nav';

      var prevBtn = document.createElement('button');
      prevBtn.type = 'button';
      prevBtn.className = 'pf-datepicker-arrow';
      prevBtn.textContent = '‹';
      prevBtn.setAttribute('aria-label', 'Previous month');
      prevBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        viewMonth -= 1;
        if (viewMonth < 0) { viewMonth = 11; viewYear -= 1; }
        renderCalendar();
      });

      var monthLabel = document.createElement('span');
      monthLabel.className = 'pf-datepicker-month';
      monthLabel.textContent = new Date(viewYear, viewMonth, 1).toLocaleString('en-GB', { month: 'long', year: 'numeric' });

      var nextBtn = document.createElement('button');
      nextBtn.type = 'button';
      nextBtn.className = 'pf-datepicker-arrow';
      nextBtn.textContent = '›';
      nextBtn.setAttribute('aria-label', 'Next month');
      nextBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        viewMonth += 1;
        if (viewMonth > 11) { viewMonth = 0; viewYear += 1; }
        renderCalendar();
      });

      nav.appendChild(prevBtn);
      nav.appendChild(monthLabel);
      nav.appendChild(nextBtn);
      calendarEl.appendChild(nav);

      var grid = document.createElement('div');
      grid.className = 'pf-datepicker-grid';
      ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].forEach(function (d) {
        var dh = document.createElement('div');
        dh.className = 'pf-datepicker-dow';
        dh.textContent = d;
        grid.appendChild(dh);
      });

      var firstOfMonth = new Date(viewYear, viewMonth, 1);
      var startOffset  = (firstOfMonth.getDay() + 6) % 7; // Monday-first grid
      var daysInMonth  = new Date(viewYear, viewMonth + 1, 0).getDate();

      for (var i = 0; i < startOffset; i++) {
        var blank = document.createElement('div');
        blank.className = 'pf-datepicker-day pf-datepicker-day-empty';
        grid.appendChild(blank);
      }

      for (var day = 1; day <= daysInMonth; day++) {
        var iso = viewYear + '-' + pad2(viewMonth + 1) + '-' + pad2(day);
        var dayBtn = document.createElement('button');
        dayBtn.type = 'button';
        dayBtn.className = 'pf-datepicker-day';
        dayBtn.textContent = String(day);
        if (iso === selectedDate) dayBtn.className += ' pf-datepicker-day-selected';

        if (dateSet[iso]) {
          dayBtn.addEventListener('click', (function (clickedDate) {
            return function (e) {
              e.stopPropagation();
              closeCalendar();
              selectedDate = clickedDate;
              toggleBtn.textContent = fmtExpiry(clickedDate);
              onSelect(clickedDate);
            };
          }(iso)));
        } else {
          dayBtn.disabled = true;
          dayBtn.className += ' pf-datepicker-day-disabled';
        }
        grid.appendChild(dayBtn);
      }

      calendarEl.appendChild(grid);
    }

    function openCalendar() {
      renderCalendar();
      calendarEl.hidden = false;
      document.addEventListener('click', onOutsideClick);
      document.addEventListener('keydown', onKeydown);
    }
    function closeCalendar() {
      calendarEl.hidden = true;
      document.removeEventListener('click', onOutsideClick);
      document.removeEventListener('keydown', onKeydown);
    }
    function onOutsideClick(e) {
      if (!pickerWrap.contains(e.target)) closeCalendar();
    }
    function onKeydown(e) {
      if (e.key === 'Escape') closeCalendar();
    }

    toggleBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      if (calendarEl.hidden) openCalendar(); else closeCalendar();
    });

    toc.appendChild(wrap);
  }

  /* Replaces the TOC date picker with a plain note, e.g. for tiers that don't get
     to pick a date (Standard members only see the last Monday's allocations). */
  function buildAllocationsDateNote(panel, message) {
    var toc = panel.querySelector('.pf-toc');
    if (!toc) return;
    var old = toc.querySelector('.pf-toc-date');
    if (old) old.remove();

    var wrap = document.createElement('div');
    wrap.className = 'pf-toc-date';

    var heading = document.createElement('div');
    heading.className = 'pf-toc-heading';
    heading.textContent = 'Date';
    wrap.appendChild(heading);

    var note = document.createElement('p');
    note.className = 'pf-toc-note';
    note.innerHTML = message;
    wrap.appendChild(note);

    toc.appendChild(wrap);
  }

  /* Replaces an entire portfolio widget with a large upgrade prompt, e.g. for
     tiers that don't get access to a given strategy's allocations. */
  function renderUpgradeNote(el, message) {
    el.innerHTML = '';
    var note = document.createElement('p');
    note.className = 'pf-upgrade-note';
    note.innerHTML = message;
    el.appendChild(note);
  }

  /* Helpers */
  function fmtCurrency(amount) {
    var abs = Math.abs(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return 'USD ' + (amount < 0 ? '-' : '') + abs;
  }
  function fmtExpiry(str) {
    var p = str.split('-');
    return p[2] + '/' + p[1] + '/' + p[0];
  }

  /* Inserts <hr> between the direct children of a container that carry content */
  function isVisibleBlock(node) {
    if (node.tagName === 'HR') return false;
    if (node.classList.contains('chart') || node.classList.contains('portfolio')) return true;
    return node.children.length > 0 || (node.textContent && node.textContent.trim() !== '');
  }
  function addSeparators(container) {
    if (!container) return;
    Array.prototype.slice.call(container.children).forEach(function (n) {
      if (n.tagName === 'HR') n.remove();
    });
    var kids = Array.prototype.filter.call(container.children, isVisibleBlock);
    kids.forEach(function (kid, i) {
      if (i > 0) container.insertBefore(document.createElement('hr'), kid);
    });
  }

  function navbarHeightPx() {
    var n = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--navbar-height'));
    return isNaN(n) ? 100 : n;
  }

  /* Builds a sticky in-page nav for a .pf-panel, from its .pf-panel-content headings.
     Highlights the link for whichever heading is currently in view (scrollspy).
     Deep-links as #<panel>/<headingId> so a refresh lands back on the same tab + anchor. */
  function buildToc(panel, selector) {
    if (!panel) return;
    var toc = panel.querySelector('.pf-toc');
    var content = panel.querySelector('.pf-panel-content');
    if (!toc || !content) return;

    if (toc._tocObserver) { toc._tocObserver.disconnect(); toc._tocObserver = null; }

    var headings = content.querySelectorAll(selector || 'h2, h3');
    toc.innerHTML = '';
    if (!headings.length) return;

    var panelName = panel.dataset.panel || 'panel';

    /* Wrapped separately so the heading-nav can be hidden on mobile while the
       download button / date picker (appended after buildToc runs) stay visible. */
    var navWrap = document.createElement('div');
    navWrap.className = 'pf-toc-nav';
    toc.appendChild(navWrap);

    var heading = document.createElement('div');
    heading.className = 'pf-toc-heading';
    heading.textContent = 'On this page';
    navWrap.appendChild(heading);

    var prefix = 'toc-' + panelName + '-';
    var used = {};
    var list = document.createElement('ul');
    var entries = []; // { heading, link }

    headings.forEach(function (h) {
      var base = prefix + (h.textContent || '').trim().toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || (prefix + 'section');
      var id = base;
      var n = 1;
      while (used[id]) { id = base + '-' + (++n); }
      used[id] = true;
      h.id = id;

      var li = document.createElement('li');
      li.className = 'pf-toc-item pf-toc-' + h.tagName.toLowerCase();
      var a = document.createElement('a');
      a.href = '#' + panelName + '/' + id;
      a.textContent = h.textContent;
      a.addEventListener('click', function (e) {
        e.preventDefault();
        h.scrollIntoView({ behavior: 'smooth', block: 'start' });
        history.replaceState(null, '', '#' + panelName + '/' + id);
      });
      li.appendChild(a);
      list.appendChild(li);
      entries.push({ heading: h, link: a });
    });

    navWrap.appendChild(list);
    entries[0].link.classList.add('active');

    var topLink = document.createElement('a');
    topLink.className = 'pf-toc-top';
    topLink.href = '#' + panelName;
    topLink.textContent = 'Back to top';
    topLink.addEventListener('click', function (e) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      history.replaceState(null, '', '#' + panelName);
    });
    navWrap.appendChild(topLink);

    var navOffset = navbarHeightPx() + 80;
    var inView = {};
    var lastActiveIdx = 0;
    /* Async content (chart titles, fetched pages, monthly data) keeps arriving and
       reshuffling headings for a moment after load/tab-switch; give it a beat to settle
       before scrollspy is allowed to overwrite the hash, so a deep link doesn't get
       clobbered by a stray observer tick while things are still loading in. */
    var armed = false;
    setTimeout(function () { armed = true; }, 600);
    var observer = new IntersectionObserver(function (obsEntries) {
      obsEntries.forEach(function (entry) {
        var idx = entries.map(function (x) { return x.heading; }).indexOf(entry.target);
        if (idx !== -1) inView[idx] = entry.isIntersecting;
      });
      var activeIdx = -1;
      for (var i = 0; i < entries.length; i++) {
        if (inView[i]) { activeIdx = i; break; }
      }
      if (activeIdx === -1) return;
      entries.forEach(function (x, i) { x.link.classList.toggle('active', i === activeIdx); });
      if (activeIdx !== lastActiveIdx) {
        lastActiveIdx = activeIdx;
        if (armed) history.replaceState(null, '', '#' + panelName + '/' + entries[activeIdx].heading.id);
      }
    }, { rootMargin: '-' + navOffset + 'px 0px -70% 0px', threshold: 0 });

    entries.forEach(function (x) { observer.observe(x.heading); });
    toc._tocObserver = observer;

    /* Deep-link restore: jump to the hash target the first time it shows up in any panel */
    if (initialHashTarget && initialHashTarget.panel === panelName && initialHashTarget.id) {
      var target = entries.filter(function (x) { return x.heading.id === initialHashTarget.id; })[0];
      if (target) {
        target.heading.scrollIntoView({ block: 'start' });
        initialHashTarget = null;
      }
    }
  }

  /* ── Positions table with inline bars ─────────────────── */
  function renderPositionsTable(container, data) {
    container.innerHTML = '';

    var nav    = data.nav || 1;
    var hasNeg = data.positions.some(function (p) { return +p.qty < 0; });

    var table = document.createElement('table');
    table.className = 'pf-table';

    var thead = document.createElement('thead');
    var htr   = document.createElement('tr');
    var cols  = posCols();
    cols.forEach(function (col) {
      var th = document.createElement('th');
      th.textContent = col.label;
      htr.appendChild(th);
    });
    if (hasNeg) { htr.appendChild(document.createElement('th')); }
    var thWeight = document.createElement('th');
    //thWeight.textContent = 'Weighting';
    thWeight.style.textAlign = 'left';
    htr.appendChild(thWeight);
    thead.appendChild(htr);
    table.appendChild(thead);

    var tbody = document.createElement('tbody');
    var rows  = posRows(data.positions);
    data.positions.forEach(function (p, idx) {
      var tr = document.createElement('tr');
      rows[idx].forEach(function (cell) {
        var td = document.createElement('td');
        td.textContent = cell;
        tr.appendChild(td);
      });

      var qty   = +p.qty || 0;
      var price = p.avg_price != null ? +p.avg_price : 0;
      var value = Math.abs(qty) * price;
      var raw   = value > 0 ? value / nav * 100 * 2 : 0;
      var barW  = value > 0 ? Math.max(1, Math.round(raw)) : 0;
      var pct   = value > 0 ? (value / nav * 100).toFixed(1) + '%' : '';

      if (hasNeg) {
        var tdNeg = document.createElement('td');
        tdNeg.className = 'pf-bar-cell pf-bar-cell-neg';
        if (qty < 0 && barW > 0) {
          var lblNeg = document.createElement('span');
          lblNeg.className = 'pf-bar-label';
          lblNeg.textContent = pct;
          var barNeg = document.createElement('div');
          barNeg.className = 'pf-bar pf-bar-neg';
          barNeg.style.width = barW + 'px';
          tdNeg.appendChild(lblNeg);
          tdNeg.appendChild(barNeg);
        }
        tr.appendChild(tdNeg);
      }

      var tdPos = document.createElement('td');
      tdPos.className = 'pf-bar-cell pf-bar-cell-pos';
      if (qty > 0 && barW > 0) {
        var barPos = document.createElement('div');
        barPos.className = 'pf-bar pf-bar-pos';
        barPos.style.width = barW + 'px';
        var lblPos = document.createElement('span');
        lblPos.className = 'pf-bar-label';
        lblPos.textContent = pct;
        tdPos.appendChild(barPos);
        tdPos.appendChild(lblPos);
      }
      tr.appendChild(tdPos);

      tbody.appendChild(tr);
    });

    /* Cash row */
    if (data.cash != null) {
      var cashValue = +data.cash || 0;
      var cashRow   = document.createElement('tr');
      var cashCells = ['Cash', '-', '-', '-', cashValue.toFixed(2), '1', '-'];
      cashCells.forEach(function (cell) {
        var td = document.createElement('td');
        td.textContent = cell;
        cashRow.appendChild(td);
      });

      var cashAbs  = Math.abs(cashValue);
      var cashRaw  = cashAbs > 0 ? cashAbs / nav * 100 * 2 : 0;
      var cashBarW = cashAbs > 0 ? Math.max(1, Math.round(cashRaw)) : 0;
      var cashPct  = cashAbs > 0 ? (cashValue < 0 ? '-' : '') + (cashAbs / nav * 100).toFixed(1) + '%' : '';

      if (hasNeg) {
        var cashTdNeg = document.createElement('td');
        cashTdNeg.className = 'pf-bar-cell pf-bar-cell-neg';
        if (cashValue < 0 && cashBarW > 0) {
          var cashLblNeg = document.createElement('span');
          cashLblNeg.className = 'pf-bar-label';
          cashLblNeg.textContent = cashPct;
          var cashBarNeg = document.createElement('div');
          cashBarNeg.className = 'pf-bar pf-bar-neg';
          cashBarNeg.style.width = cashBarW + 'px';
          cashTdNeg.appendChild(cashLblNeg);
          cashTdNeg.appendChild(cashBarNeg);
        }
        cashRow.appendChild(cashTdNeg);
      }

      var cashTdPos = document.createElement('td');
      cashTdPos.className = 'pf-bar-cell pf-bar-cell-pos';
      if (cashValue >= 0 && cashBarW > 0) {
        var cashBarPos = document.createElement('div');
        cashBarPos.className = 'pf-bar pf-bar-pos';
        cashBarPos.style.width = cashBarW + 'px';
        var cashLblPos = document.createElement('span');
        cashLblPos.className = 'pf-bar-label';
        cashLblPos.textContent = cashPct;
        cashTdPos.appendChild(cashBarPos);
        cashTdPos.appendChild(cashLblPos);
      }
      cashRow.appendChild(cashTdPos);
      tbody.appendChild(cashRow);
    }

    table.appendChild(tbody);
    var wrap = document.createElement('div');
    wrap.className = 'pf-table-wrap';
    wrap.appendChild(table);
    container.appendChild(wrap);
  }

  /* ── Term structure chart ──────────────────────────────── */
  function initTermStructure(placeholder) {
    if (!placeholder) return;

    var date  = (placeholder.dataset.date  || '').trim();
    var title = (placeholder.dataset.title || '').trim() || 'Term structure';

    var card = document.createElement('div');
    card.className = 'chart-card chart-card--inline';
    var _parent = placeholder.parentNode;
    _parent.insertBefore(card, placeholder);
    while (placeholder.firstChild) { _parent.insertBefore(placeholder.firstChild, placeholder); }
    _parent.removeChild(placeholder);

    var titleEl = document.createElement('h3');
    titleEl.className = 'pf-title';
    titleEl.textContent = title;
    card.appendChild(titleEl);

    var legendEl = document.createElement('div');
    legendEl.className = 'chart-legend';
    card.appendChild(legendEl);

    var chartEl = document.createElement('div');
    Object.assign(chartEl.style, { width: '100%', position: 'relative' });
    card.appendChild(chartEl);

    var loadingEl = document.createElement('div');
    Object.assign(loadingEl.style, {
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '300px', color: 'rgba(255,255,255,0.35)', fontSize: '0.85rem',
    });
    loadingEl.textContent = 'Loading...';
    chartEl.appendChild(loadingEl);

    var CATEGORIES = ['9d VIX', '30d VIX', '3m VIX', '1y VIX'];
    var COLORS = ['#094385', '#E7574E'];

    var url = API_BASE + '/api/term' + (date ? '?date=' + encodeURIComponent(date) : '');
    fetch(url)
      .then(function (r) { return r.json(); })
      .then(function (series) {
        loadingEl.remove();

        legendEl.innerHTML = series.map(function (s, i) {
          return '<span><span class="chart-legend-dot" style="background:' + (COLORS[i] || '#fff') + ';"></span>' + s.name + '</span>';
        }).join('');

        var PAD = { top: 20, right: 20, bottom: 44, left: 48 };
        var VW = 600, VH = 300;
        var innerW = VW - PAD.left - PAD.right;
        var innerH = VH - PAD.top  - PAD.bottom;

        var allVals = series.reduce(function (a, s) { return a.concat(s.data); }, []);
        var minV = Math.min.apply(null, allVals);
        var maxV = Math.max.apply(null, allVals);
        var spread = maxV - minV || 1;
        minV -= spread * 0.1;
        maxV += spread * 0.1;

        var step = innerW / (CATEGORIES.length - 1);
        function xOf(i) { return PAD.left + i * step; }
        function yOf(v) { return PAD.top + innerH - ((v - minV) / (maxV - minV)) * innerH; }

        function svgEl(tag, attrs) {
          var e = document.createElementNS('http://www.w3.org/2000/svg', tag);
          Object.keys(attrs).forEach(function (k) { e.setAttribute(k, attrs[k]); });
          return e;
        }
        function svgText(x, y, anchor, fill, size, content) {
          var t = svgEl('text', { x: x, y: y, 'text-anchor': anchor, fill: fill, 'font-size': size, 'font-family': 'inherit' });
          t.textContent = content;
          return t;
        }

        var svg = svgEl('svg', { viewBox: '0 0 ' + VW + ' ' + VH, preserveAspectRatio: 'xMidYMid meet' });
        Object.assign(svg.style, { width: '100%', height: 'auto', display: 'block', overflow: 'visible' });

        /* Y grid lines + labels */
        var yTicks = 5;
        for (var t = 0; t <= yTicks; t++) {
          var v = minV + (maxV - minV) * (t / yTicks);
          var y = yOf(v);
          svg.appendChild(svgEl('line', { x1: PAD.left, y1: y, x2: VW - PAD.right, y2: y, stroke: 'rgba(255,255,255,0.08)', 'stroke-width': '1' }));
          svg.appendChild(svgText(PAD.left - 6, y, 'end', 'rgba(255,255,255,0.35)', '11', v.toFixed(1)));
          svg.querySelector('text:last-child') && svg.lastChild.setAttribute('dominant-baseline', 'middle');
        }

        /* X labels */
        CATEGORIES.forEach(function (cat, i) {
          svg.appendChild(svgText(xOf(i), VH - PAD.bottom + 18, 'middle', 'rgba(255,255,255,0.5)', '11', cat));
        });

        /* Series */
        series.forEach(function (s, si) {
          var color = COLORS[si] || '#fff';
          var pts = s.data.map(function (v, i) { return xOf(i) + ',' + yOf(v); }).join(' ');
          svg.appendChild(svgEl('polyline', { points: pts, fill: 'none', stroke: color, 'stroke-width': '2', 'stroke-linejoin': 'round' }));
          s.data.forEach(function (v, i) {
            svg.appendChild(svgEl('circle', { cx: xOf(i), cy: yOf(v), r: '4', fill: color }));
          });
        });

        chartEl.appendChild(svg);
      })
      .catch(function (err) { console.error('Term structure fetch error:', err); });
  }

  /* Reads the signed-in member's tier (Standard / Pro / Plus / Business) from their
     active subscription, exposed server-side as window.memberSubscriptions. */
  function getMemberTier() {
    var subs = window.memberSubscriptions || [];
    var active = subs.filter(function (s) {
      return s && s.tier && (s.status === 'active' || s.status === 'trialing');
    })[0];
    return active ? active.tier.name : null;
  }

  /* Whether the signed-in member's tier is included in a portfolio's `tiers`
     list (e.g. ["plus", "business"]). Portfolios without a `tiers` list are
     open to everyone. */
  function memberHasPortfolioAccess(pf) {
    if (!pf.tiers || !pf.tiers.length) return true;
    var tier = getMemberTier();
    return !!tier && pf.tiers.indexOf(tier) !== -1;
  }

  /* ── Portfolio cover (4 large tabs: allocations / performance / monthly / spec) ── */
  function initPortfolioCover() {
    var root = document.querySelector('.portfolio-cover');
    if (!root) return;

    var mp = window.model_portfolio;
    var hasMp = !!(mp && mp.id != null);
    var isBusinessMember = getMemberTier() === 'Business';

    var requiredEls = root.querySelectorAll('[data-mp-required]');
    if (!hasMp) {
      requiredEls.forEach(function (n) { n.remove(); });
    } else {
      var pfEl = root.querySelector('.pf-panel[data-panel="allocations"] .portfolio');
      if (pfEl) pfEl.dataset.strategy = mp.id;

      var chartEl = root.querySelector('.pf-panel[data-panel="performance"] .chart');
      if (chartEl) {
        chartEl.dataset.strategy = mp.id;
        if (mp.benchmark) chartEl.dataset.benchmark = mp.benchmark;
      }

      var perfPanel = root.querySelector('.pf-panel[data-panel="performance"]');
      if (perfPanel) {
        var perfContent = perfPanel.querySelector('.pf-panel-content');
        movePageIntoSlot('backtest-methodology', perfPanel.querySelector('.ctabs-backtest-generic'));
        if (mp.backtest) movePageIntoSlot(mp.backtest, perfPanel.querySelector('.ctabs-backtest-specific'));
        addSeparators(perfContent);
        buildToc(perfPanel);
      }

      /* Monthly reporting is a business-tier feature */
      if (isBusinessMember) {
        initMonthlyReport(root, mp);
      } else {
        root.querySelectorAll('.cover-tab[data-panel="monthly"], .pf-panel[data-panel="monthly"]')
          .forEach(function (n) { n.remove(); });
      }
    }

    var specPanel = root.querySelector('.pf-panel[data-panel="spec"]');
    if (specPanel) {
      var specContent = specPanel.querySelector('.pf-panel-content');
      if (isBusinessMember) {
        movePageIntoSlot('common-strategy-specification', specPanel.querySelector('.ctabs-spec-generic'));
        if (hasMp && mp.spec) movePageIntoSlot(mp.spec, specPanel.querySelector('.ctabs-spec-specific'));
        var complianceSlot = specPanel.querySelector('.ctabs-spec-compliance');
        movePageIntoSlot('risk-disclosure-statement', complianceSlot);
        movePageIntoSlot('suitability-framework', complianceSlot);
        addSeparators(complianceSlot);
      } else {
        /* Non-Business tiers: only the post's own content shows, none of the spec/compliance pages */
        specPanel.querySelectorAll('.ctabs-spec-generic, .ctabs-spec-specific, .ctabs-spec-compliance')
          .forEach(function (n) { n.remove(); });
      }
      addSeparators(specContent);
      buildToc(specPanel, 'h2');
      buildTocDownload(specPanel, coverPostTitle() + ' - Strategy specification');
    }

    initCoverTabs(root, hasMp);
    initStickyCoverTabs(root);
  }

  function initCoverTabs(root, hasMp) {
    var tabs   = root.querySelectorAll('.cover-tab');
    var panels = root.querySelectorAll('.pf-panel');

    var hashTab = initialHashTarget
      ? root.querySelector('.cover-tab[data-panel="' + initialHashTarget.panel + '"]')
      : null;

    if (hashTab) {
      tabs.forEach(function (t) { t.classList.remove('active'); });
      panels.forEach(function (p) { p.classList.remove('active'); });
      hashTab.classList.add('active');
      var hashPanel = root.querySelector('.pf-panel[data-panel="' + initialHashTarget.panel + '"]');
      if (hashPanel) hashPanel.classList.add('active');
    } else if (!hasMp && tabs.length && panels.length) {
      tabs[0].classList.add('active');
      panels[0].classList.add('active');
    }

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        tabs.forEach(function (t) { t.classList.remove('active'); });
        panels.forEach(function (p) { p.classList.remove('active'); });
        tab.classList.add('active');
        var panel = root.querySelector('.pf-panel[data-panel="' + tab.dataset.panel + '"]');
        if (panel) panel.classList.add('active');
        history.replaceState(null, '', '#' + tab.dataset.panel);
        /* Jump straight to where the tab bar pins, so the panel opens with the
           bar already in its compact fixed form instead of a screen of cover. */
        window.scrollTo({ top: PIN_SCROLL_Y, behavior: 'smooth' });
      });
    });
  }

  /* Pins the tab bar just below the header once scrolling would otherwise carry it
     off-screen with the rest of the cover. Uses a placeholder (inserted after .cover)
     to hold its layout space so unpinning it doesn't jump the page.

     .cover-tabs stays out of document flow the whole time (position:absolute at
     rest, position:fixed once pinned) on every breakpoint, so toggling .is-pinned
     never changes anyone else's layout — nothing to jump. Since the tabs no longer
     contribute to .cover's height via flow, .cover's own padding-bottom is measured
     against the tabs' actual rendered height instead, so it still reserves enough
     room for the bar (single row on desktop, stacked column on mobile) without
     overlapping the title above it. */
  function initStickyCoverTabs(root) {
    var cover = root.querySelector('.cover');
    var tabs = root.querySelector('.cover-tabs');
    if (!cover || !tabs) return;

    var pinned = false;

    function reserveSpace() {
      if (!pinned) cover.style.paddingBottom = (tabs.offsetHeight + 30) + 'px';
    }

    function update() {
      var shouldPin = window.innerWidth > 767 && window.scrollY >= PIN_SCROLL_Y;
      if (shouldPin && !pinned) {
        pinned = true;
        tabs.classList.add('is-pinned');
        /* So TOC scroll targets (scroll-margin-top) can account for the pinned
           bar sitting below the header, in addition to the header itself. */
        document.documentElement.style.setProperty('--pinned-tabs-height', tabs.offsetHeight + 'px');
      } else if (!shouldPin && pinned) {
        pinned = false;
        tabs.classList.remove('is-pinned');
        document.documentElement.style.setProperty('--pinned-tabs-height', '0px');
        reserveSpace();
      }
    }

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', function () { reserveSpace(); update(); });
    reserveSpace();
    update();
  }

  /* Moves a broad-fetched Ghost page (see .gh-page-store) into a panel slot */
  function movePageIntoSlot(slug, slot) {
    if (!slug || !slot) return;
    var store = document.querySelector('.gh-page-store');
    if (!store) return;
    var pages = store.querySelectorAll('.gh-page');
    for (var i = 0; i < pages.length; i++) {
      if (pages[i].dataset.slug === slug) { slot.appendChild(pages[i]); return; }
    }
  }

  /* ── Monthly report tab ─────────────────────────────────── */
  function initMonthlyReport(root, mp) {
    var panel  = root.querySelector('.pf-panel[data-panel="monthly"]');
    if (!panel) return;
    var bodyEl = panel.querySelector('.mr-body');
    if (!bodyEl) return;

    authFetch(API_BASE + '/api/monthly/reports')
      .then(function (r) { return r.json(); })
      .then(function (dates) {
        if (!dates || !dates.length) return;
        dates = dates.slice().sort();
        loadMonthlyReport(panel, mp, dates, dates[dates.length - 1], bodyEl);
      })
      .catch(function (err) { console.error('Monthly reports fetch error:', err); });
  }

  /* Monthly write-ups (market update + per-portfolio strategy update) are
     Ghost posts, one new pair every month with no upper bound — unlike the
     handful of generic pages in .gh-page-store, they're never bulk-fetched
     into the page. Instead each is pulled on demand, by slug, from Ghost's
     public Content API as the reader picks a date, and cached here so
     flipping back to an already-seen date doesn't refetch it. */
  var ghostPostCache = {};
  function fetchGhostPost(slug) {
    if (!slug) return Promise.resolve(null);
    if (Object.prototype.hasOwnProperty.call(ghostPostCache, slug)) {
      return Promise.resolve(ghostPostCache[slug]);
    }
    if (!window.contentApiKey) return Promise.resolve(null);
    var url = '/ghost/api/content/posts/slug/' + encodeURIComponent(slug) + '/'
      + '?key=' + encodeURIComponent(window.contentApiKey)
      + '&fields=title,html,feature_image,feature_image_alt';
    return fetch(url)
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (json) {
        var post = json && json.posts && json.posts[0] ? json.posts[0] : null;
        ghostPostCache[slug] = post;
        return post;
      })
      .catch(function (err) { console.error('Ghost content fetch error:', err); return null; });
  }

  /* Rebuilds a moved-page's markup (title, optional feature image, body)
     from a Content API post, matching what {{#get}} rendered server-side
     for the pages still fetched in bulk (backtest/spec/compliance). */
  function appendGhostPost(container, post) {
    if (!post) return;
    var wrap = document.createElement('div');
    wrap.className = 'mr-content';
    var h = document.createElement('h2');
    h.textContent = post.title || '';
    wrap.appendChild(h);
    if (post.feature_image) {
      var img = document.createElement('img');
      img.src = post.feature_image;
      img.alt = post.feature_image_alt || post.title || '';
      wrap.appendChild(img);
    }
    var body = document.createElement('div');
    body.innerHTML = post.html || ''; // already-rendered, sanitized Ghost HTML
    wrap.appendChild(body);
    container.appendChild(wrap);
  }

  function loadMonthlyReport(panel, mp, dates, date, bodyEl) {
    bodyEl.innerHTML = '<div class="pf-loading">Loading…</div>';
    Promise.all([
      authFetch(API_BASE + '/api/monthly/' + mp.id + '?date=' + encodeURIComponent(date))
        .then(function (r) { return r.ok ? r.json() : {}; }),
      fetchGhostPost('monthly-update-' + date),
      mp.monthly ? fetchGhostPost(mp.monthly.replace('{date}', date)) : Promise.resolve(null),
    ])
      .then(function (results) {
        renderMonthlyReport(panel, mp, dates, date, results[0], results[1], results[2], bodyEl);
      })
      .catch(function (err) { console.error('Monthly report fetch error:', err); });
  }

  /* Rebuilt after every buildToc() call so the date picker always ends up
     as the last item in the in-page nav, current on the selected date. */
  function buildMonthlyDateSelect(panel, mp, dates, selectedDate, bodyEl) {
    var toc = panel.querySelector('.pf-toc');
    if (!toc) return;
    var old = toc.querySelector('.pf-toc-date');
    if (old) old.remove();

    var wrap = document.createElement('div');
    wrap.className = 'pf-toc-date';

    var label = document.createElement('label');
    label.className = 'pf-toc-heading';
    label.textContent = 'Report date';
    label.setAttribute('for', 'mr-date-select');
    wrap.appendChild(label);

    var select = document.createElement('select');
    select.id = 'mr-date-select';
    select.className = 'pf-toc-select';
    dates.forEach(function (d) {
      var opt = document.createElement('option');
      opt.value = d;
      opt.textContent = d;
      if (d === selectedDate) opt.selected = true;
      select.appendChild(opt);
    });
    select.addEventListener('change', function () {
      loadMonthlyReport(panel, mp, dates, select.value, bodyEl);
    });
    wrap.appendChild(select);

    toc.appendChild(wrap);
  }

  function renderMonthlyReport(panel, mp, dates, date, data, updatePost, strategyPost, bodyEl) {
    bodyEl.innerHTML = '';

    appendGhostPost(bodyEl, updatePost);

    if (data.vrp) {
      var vrp = data.vrp;
      renderTable(appendSection(bodyEl, 'Realized variance risk premium'), {
        cols: [
          { label: 'Metric', align: '' },
          { label: 'Value',  align: 'r' },
        ],
        rows: [
          ['SPX realized vol over period', (+vrp.realized_vol_pts).toFixed(2) + ' pts'],
          ['VIX at period start',          (+vrp.vix_at_period_start_pts).toFixed(2) + ' pts'],
          ['VRP',                          fmtSignedPts(vrp.vrp_pts)],
        ],
      });
    }

    if (data.instruments && data.instruments.length) {
      renderTable(appendSection(bodyEl, 'Instrument performance'), {
        cols: [
          { label: 'Instrument',    align: '' },
          { label: 'Period return', align: 'r' },
          { label: 'YTD',           align: 'r' },
          { label: '3Y',            align: 'r' },
        ],
        rows: data.instruments.map(function (row) {
          return [row.instrument, fmtSignedPct(row.period_return_pct), fmtSignedPct(row.ytd_pct), fmtSignedPct(row.three_year_pct)];
        }),
      });
    }

    appendGhostPost(bodyEl, strategyPost);

    if (data.strategy_performance) {
      var sp = data.strategy_performance;
      renderTable(appendSection(bodyEl, 'Strategy performance'), {
        cols: [
          { label: 'Metric',    align: '' },
          { label: 'Strategy',  align: 'r' },
          { label: 'Benchmark', align: 'r' },
          { label: 'Diff',      align: 'r' },
        ],
        rows: [
          ['Period return', fmtSignedPct(sp.period_return_strategy_pct), fmtSignedPct(sp.period_return_benchmark_pct), fmtSignedPct(sp.diff_pct)],
          ['CAGR',           fmtSignedPct(sp.cagr_strategy_pct),          fmtSignedPct(sp.cagr_benchmark_pct),          fmtSignedPct(sp.cagr_diff_pct)],
        ],
      });
    }

    addSeparators(bodyEl);
    buildToc(panel);
    buildMonthlyDateSelect(panel, mp, dates, date, bodyEl);
    buildTocDownload(panel, coverPostTitle() + ' - Monthly report - ' + date);
  }

  function appendSection(container, title) {
    var wrap = document.createElement('div');
    wrap.className = 'mr-content';
    var h = document.createElement('h3');
    h.className = 'pf-section-title';
    h.textContent = title;
    wrap.appendChild(h);
    var body = document.createElement('div');
    wrap.appendChild(body);
    container.appendChild(wrap);
    return body;
  }

  function fmtSignedPct(v) {
    v = +v;
    return (v >= 0 ? '+' : '') + v.toFixed(2) + '%';
  }
  function fmtSignedPts(v) {
    v = +v;
    return (v >= 0 ? '+' : '') + v.toFixed(2) + ' pts';
  }

  /* ── Portfolio tabs (cover page) ──────────────────────── */
  function initPortfolioTabs() {
    var el = document.querySelector('.portfolio-tabs');
    if (!el || !window.portfolios || !window.portfolios.length) return;

    var portfolios = window.portfolios;

    el.innerHTML = '';

    var heading = document.createElement('h5');
    heading.textContent = 'Our portfolios';
    el.appendChild(heading);

    var heading = document.createElement('h2');
    heading.textContent = 'Model portfolios to suit your objectives.';
    heading.className = "gradient";
    el.appendChild(heading);

    var wrapper = document.createElement('div');
    wrapper.className = 'ptabs';
    el.appendChild(wrapper);

    var list = document.createElement('div');
    list.className = 'ptabs-list';
    wrapper.appendChild(list);

    var panelsEl = document.createElement('div');
    panelsEl.className = 'ptabs-panels';
    wrapper.appendChild(panelsEl);

    var tabEls = [];
    var panelEls = [];

    portfolios.forEach(function (pf, i) {
      var tab = document.createElement('div');
      tab.className = 'ptabs-tab' + (i === 0 ? ' active' : '');

      var nameEl = document.createElement('span');
      nameEl.className = 'ptabs-tab-name';
      nameEl.textContent = pf.name || '';
      tab.appendChild(nameEl);

      var summaryEl = document.createElement('span');
      summaryEl.className = 'ptabs-tab-summary';
      summaryEl.textContent = pf.summary || '';
      tab.appendChild(summaryEl);

      var hasAccess = memberHasPortfolioAccess(pf);

      var tabLinkEl = document.createElement('a');
      tabLinkEl.className = 'button ptabs-tab-link' + (hasAccess ? '' : ' ptabs-tab-link-disabled');
      tabLinkEl.textContent = 'View strategy';
      if (hasAccess) {
        tabLinkEl.href = pf.url || '#';
      } else {
        tabLinkEl.setAttribute('aria-disabled', 'true');
      }
      tabLinkEl.addEventListener('click', function (e) {
        e.stopPropagation();
        if (!hasAccess) e.preventDefault();
      });
      tab.appendChild(tabLinkEl);

      tab.addEventListener('click', function () {
        tabEls.forEach(function (t) { t.classList.remove('active'); });
        panelEls.forEach(function (p) { p.classList.remove('active'); });
        tab.classList.add('active');
        panelEls[i].classList.add('active');
      });

      list.appendChild(tab);
      tabEls.push(tab);

      var panel = document.createElement('div');
      panel.className = 'ptabs-panel' + (i === 0 ? ' active' : '');

      var chartDiv = document.createElement('div');
      chartDiv.className = 'chart';
      if (pf.strategy != null) chartDiv.dataset.strategy = pf.strategy;
      if (pf.benchmark) chartDiv.dataset.benchmark = pf.benchmark;
      if (pf.name) chartDiv.dataset.title = pf.name;
      panel.appendChild(chartDiv);

      panelsEl.appendChild(panel);
      panelEls.push(panel);
    });
  }

  /* ── Bootstrap ─────────────────────────────────────────── */
  initPortfolioCover();
  initPortfolio();

  /* window.portfolios is set via Ghost footer code injection, which renders after
     this <script> tag (see {{ghost_foot}} in default.hbs) — so it isn't available
     yet if we read it synchronously here. Run immediately if it's already there
     (e.g. injected in the header instead), otherwise wait for DOMContentLoaded,
     by which point any footer-injected script has already run. Chart init is
     bundled into the same deferred step because resolvePortfolioSlugs() must
     finish setting data-strategy before initChart() runs for those elements. */
  function runPortfolioDependentBootstrap() {
    initPortfolioTabs();

    /* Disable the link on model portfolio cards the signed-in member's tier
       doesn't include — the card itself still shows. */
    (function gateInaccessiblePortfolios() {
      var pfs = window.portfolios;
      if (!pfs || !pfs.length) return;

      document.querySelectorAll('.mu-item-portfolio[data-title]').forEach(function (card) {
        var match = pfs.filter(function (pf) { return pf.name === card.dataset.title; })[0];
        if (!match || memberHasPortfolioAccess(match)) return;

        card.classList.add('mu-item-portfolio-disabled');
        var link = card.querySelector('.u-permalink');
        if (link) {
          link.removeAttribute('href');
          link.setAttribute('aria-disabled', 'true');
          link.addEventListener('click', function (e) { e.preventDefault(); });
        }
      });
    }());

    /* Resolve data-slug → data-strategy/data-benchmark for portfolio feed cards */
    (function resolvePortfolioSlugs() {
      var pfs = window.portfolios;
      if (!pfs || !pfs.length) return;
      var els = document.querySelectorAll('.chart[data-slug]');
      for (var i = 0; i < els.length; i++) {
        var el = els[i];
        var slug = el.dataset.slug;
        for (var j = 0; j < pfs.length; j++) {
          if (pfs[j].url === '/' + slug || pfs[j].url === slug) {
            el.dataset.strategy = pfs[j].strategy;
            if (pfs[j].benchmark) el.dataset.benchmark = pfs[j].benchmark;
            el.removeAttribute('data-slug');
            break;
          }
        }
      }
    }());

    var termEls = document.querySelectorAll('.chart.term-structure');
    termEls.forEach(function (el) { initTermStructure(el); });

    var chartEls = document.querySelectorAll('.chart:not(.term-structure)');
    if (chartEls.length) {
      var runCharts = function () { chartEls.forEach(function (el) { initChart(el); }); };
      if (window.LightweightCharts) {
        runCharts();
      } else {
        var s = document.createElement('script');
        s.src = 'https://unpkg.com/lightweight-charts/dist/lightweight-charts.standalone.production.js';
        s.onload = runCharts;
        s.onerror = function () { console.error('Failed to load LightweightCharts'); };
        document.head.appendChild(s);
      }
    }
  }

  if (window.portfolios) {
    runPortfolioDependentBootstrap();
  } else {
    document.addEventListener('DOMContentLoaded', runPortfolioDependentBootstrap);
  }

}());